import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatTimeInTz, getActiveTimezone } from '../../lib/dateUtils';
import {
  RefreshCw,
  Plus,
  Trash2,
  Save,
  Check,
  X,
  Volume2,
  Maximize2,
  Minimize2,
  Coffee,
  AlertTriangle,
  Moon,
  Sparkles,
  Layers,
  Edit3,
  Monitor,
  Info,
  Tv,
  Briefcase,
  Users,
  GripHorizontal,
  Move,
  Settings2,
  Building2,
  DoorOpen,
  LayoutGrid,
  ChevronDown,
  ShieldAlert,
  Sliders,
  CheckCircle2,
} from 'lucide-react';
import { IosBottomSheet, WorkstationSheetData } from '../../components/ios/IosBottomSheet';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

export type CabinType = 'cabin' | 'bay' | 'conference' | 'lounge' | 'server';

export interface Cabin {
  id: string;
  name: string;
  type: CabinType;
  color: string;
  x: number; // 1-12
  y: number; // 1-8
  w: number; // grid columns
  h: number; // grid rows
}

export interface Desk {
  id: string;
  label: string;
  deviceId?: string | null;
  x: number;
  y: number;
  cabinId?: string | null;
}

export interface OfficeFloor {
  id: string;
  name: string;
  floorLevel: string;
  cabins: Cabin[];
  desks: Desk[];
}

export interface FloorPlanPayload {
  gridWidth: number;
  gridHeight: number;
  offices: OfficeFloor[];
  activeOfficeId: string;
  // Backwards compatibility with previous versions:
  zones?: any[];
  desks?: any[];
}

const CABIN_COLORS: Record<string, { bg: string; border: string; text: string; tint: string; glow: string }> = {
  emerald: {
    bg: 'bg-emerald-500/5',
    border: 'border-emerald-500/40',
    text: 'text-emerald-400',
    tint: 'bg-emerald-500',
    glow: 'rgba(16, 185, 129, 0.12)',
  },
  purple: {
    bg: 'bg-purple-500/5',
    border: 'border-purple-500/40',
    text: 'text-purple-400',
    tint: 'bg-purple-500',
    glow: 'rgba(168, 85, 247, 0.12)',
  },
  blue: {
    bg: 'bg-blue-500/5',
    border: 'border-blue-500/40',
    text: 'text-blue-400',
    tint: 'bg-blue-500',
    glow: 'rgba(59, 130, 246, 0.12)',
  },
  amber: {
    bg: 'bg-amber-500/5',
    border: 'border-amber-500/40',
    text: 'text-amber-400',
    tint: 'bg-amber-500',
    glow: 'rgba(245, 158, 11, 0.12)',
  },
  rose: {
    bg: 'bg-rose-500/5',
    border: 'border-rose-500/40',
    text: 'text-rose-400',
    tint: 'bg-rose-500',
    glow: 'rgba(244, 63, 94, 0.12)',
  },
  indigo: {
    bg: 'bg-indigo-500/5',
    border: 'border-indigo-500/40',
    text: 'text-indigo-400',
    tint: 'bg-indigo-500',
    glow: 'rgba(99, 102, 241, 0.12)',
  },
  cyan: {
    bg: 'bg-cyan-500/5',
    border: 'border-cyan-500/40',
    text: 'text-cyan-400',
    tint: 'bg-cyan-500',
    glow: 'rgba(6, 182, 212, 0.12)',
  },
  zinc: {
    bg: 'bg-zinc-500/5',
    border: 'border-zinc-500/40',
    text: 'text-zinc-400',
    tint: 'bg-zinc-500',
    glow: 'rgba(113, 113, 122, 0.12)',
  },
};

export function FloorPlanPage() {
  const { user } = useAuth();
  const orgTimezone = getActiveTimezone(user?.organization?.timezone);

  const canvasRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Core Multi-Office State
  const [offices, setOffices] = useState<OfficeFloor[]>([]);
  const [activeOfficeId, setActiveOfficeId] = useState<string>('office-default');
  const [gridWidth] = useState(12);
  const [gridHeight] = useState(8);

  const [workstations, setWorkstations] = useState<any[]>([]);
  const [metrics, setMetrics] = useState({
    total: 0,
    active: 0,
    onBreak: 0,
    inactive: 0,
    shiftEnded: 0,
    offline: 0,
  });

  // Selected desk for inspector sheet
  const [sheetWorkstation, setSheetWorkstation] = useState<WorkstationSheetData | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [ringingDeviceId, setRingingDeviceId] = useState<string | null>(null);
  const [pingSuccessDeskId, setPingSuccessDeskId] = useState<string | null>(null);
  const [ringFeedback, setRingFeedback] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Modals state
  const [showDeskModal, setShowDeskModal] = useState(false);
  const [editingDesk, setEditingDesk] = useState<Desk | null>(null);

  const [showCabinModal, setShowCabinModal] = useState(false);
  const [editingCabin, setEditingCabin] = useState<Cabin | null>(null);

  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [newOfficeName, setNewOfficeName] = useState('');
  const [newOfficeFloor, setNewOfficeFloor] = useState('Floor 1');
  const [newOfficeTemplate, setNewOfficeTemplate] = useState<'EXECUTIVE' | 'TECH' | 'BLANK'>('EXECUTIVE');

  // Dragging State
  const [draggingDeskId, setDraggingDeskId] = useState<string | null>(null);
  const [dragGhostPos, setDragGhostPos] = useState<{ leftPct: number; topPct: number } | null>(null);
  const [dragTargetCell, setDragTargetCell] = useState<{ col: number; row: number } | null>(null);
  const [dragStartCoord, setDragStartCoord] = useState<{ x: number; y: number } | null>(null);

  // Live ticking timer for elapsed seconds
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Current active office object
  const currentOffice: OfficeFloor = offices.find((o) => o.id === activeOfficeId) || offices[0] || {
    id: 'office-default',
    name: 'Main Headquarters',
    floorLevel: 'Floor 1',
    cabins: [],
    desks: [],
  };

  // Convert raw API response into multi-office structure
  const parseServerPlan = (rawPlan: any, serverWorkstations: any[]): { offices: OfficeFloor[]; activeId: string } => {
    if (!rawPlan) {
      return generateInitialOffice(serverWorkstations);
    }

    // Modern multi-office format
    if (Array.isArray(rawPlan.offices) && rawPlan.offices.length > 0) {
      return {
        offices: rawPlan.offices,
        activeId: rawPlan.activeOfficeId || rawPlan.offices[0].id,
      };
    }

    // Backwards compatibility with previous zones/desks format
    if (Array.isArray(rawPlan.zones) || Array.isArray(rawPlan.desks)) {
      const cabins: Cabin[] = (rawPlan.zones || []).map((z: any) => ({
        id: z.id,
        name: z.name || 'Office Bay',
        type: z.type || (z.name?.toLowerCase().includes('suite') || z.name?.toLowerCase().includes('board') ? 'conference' : z.name?.toLowerCase().includes('lounge') || z.name?.toLowerCase().includes('coffee') ? 'lounge' : z.name?.toLowerCase().includes('cabin') ? 'cabin' : 'bay'),
        color: z.color || 'emerald',
        x: z.x || 1,
        y: z.y || 1,
        w: z.w || 4,
        h: z.h || 4,
      }));

      const desks: Desk[] = (rawPlan.desks || []).map((d: any) => ({
        id: d.id,
        label: d.label || 'Desk',
        deviceId: d.deviceId || null,
        x: d.x || 2,
        y: d.y || 2,
        cabinId: d.zoneId || d.cabinId || null,
      }));

      const defaultOffice: OfficeFloor = {
        id: 'office-main',
        name: 'Main Headquarters',
        floorLevel: 'Floor 1',
        cabins,
        desks,
      };

      return {
        offices: [defaultOffice],
        activeId: 'office-main',
      };
    }

    return generateInitialOffice(serverWorkstations);
  };

  // Initial balanced office with Executive Cabins and Tech Bay
  const generateInitialOffice = (serverWorkstations: any[]): { offices: OfficeFloor[]; activeId: string } => {
    const initialCabins: Cabin[] = [
      { id: 'cab-md', name: 'Director Cabin 01', type: 'cabin', color: 'purple', x: 1, y: 1, w: 3.8, h: 3.5 },
      { id: 'cab-exec2', name: 'Executive Suite 02', type: 'cabin', color: 'indigo', x: 1, y: 4.8, w: 3.8, h: 3.2 },
      { id: 'cab-eng', name: 'Engineering & DevOps Pod', type: 'bay', color: 'emerald', x: 5.2, y: 1, w: 4.2, h: 7 },
      { id: 'cab-board', name: 'Glass Boardroom', type: 'conference', color: 'cyan', x: 9.7, y: 1, w: 3.3, h: 4 },
      { id: 'cab-cafe', name: 'Pantry & Coffee Lounge', type: 'lounge', color: 'amber', x: 9.7, y: 5.2, w: 3.3, h: 2.8 },
    ];

    const initialDesks: Desk[] = serverWorkstations.map((w: any, idx: number) => {
      let x = 6;
      let y = 2;
      let cabinId = 'cab-eng';

      if (idx === 0) {
        // Director Cabin
        x = 2; y = 2.2; cabinId = 'cab-md';
      } else if (idx === 1) {
        // Engineering Pod Station A
        x = 6; y = 2.2; cabinId = 'cab-eng';
      } else if (idx === 2) {
        // Engineering Pod Station B
        x = 6; y = 5; cabinId = 'cab-eng';
      } else if (idx === 3) {
        // Executive Suite 2
        x = 2; y = 5.8; cabinId = 'cab-exec2';
      } else {
        x = 7.5; y = (idx % 2 === 0 ? 2.2 : 5);
        cabinId = 'cab-eng';
      }

      return {
        id: `desk-${idx + 1}`,
        label: `Desk ${String(idx + 1).padStart(2, '0')}`,
        deviceId: w.deviceId,
        x,
        y,
        cabinId,
      };
    });

    const office: OfficeFloor = {
      id: 'office-main',
      name: 'Main Headquarters',
      floorLevel: 'Floor 1',
      cabins: initialCabins,
      desks: initialDesks,
    };

    return {
      offices: [office],
      activeId: 'office-main',
    };
  };

  const loadData = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await api.admin.getFloorPlan();
      const rawWs = res.workstations || [];
      setWorkstations(rawWs);

      const parsed = parseServerPlan(res.floorPlan, rawWs);
      setOffices(parsed.offices);
      setActiveOfficeId(parsed.activeId);

      if (res.metrics) {
        setMetrics({
          total: res.metrics.total || 0,
          active: res.metrics.active || 0,
          onBreak: res.metrics.onBreak || 0,
          inactive: res.metrics.inactive || 0,
          shiftEnded: res.metrics.shiftEnded || 0,
          offline: res.metrics.offline || 0,
        });
      }
    } catch (err) {
      console.error('Failed to load floor plan data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData(true);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Save floor plan configuration
  const handleSaveFloorPlan = async () => {
    setSaving(true);
    try {
      const payload: FloorPlanPayload = {
        gridWidth,
        gridHeight,
        offices,
        activeOfficeId,
        zones: currentOffice.cabins,
        desks: currentOffice.desks,
      };

      await api.admin.updateFloorPlan(payload);
      haptics.notificationSuccess();
      sounds.playChimeSound();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setIsEditMode(false);
    } catch (err) {
      console.error('Failed to save floor plan:', err);
      haptics.notificationError();
    } finally {
      setSaving(false);
    }
  };

  // Workstation mapping helper
  const getWorkstationForDesk = (desk: Desk) => {
    if (!desk.deviceId) return null;
    return workstations.find((w) => w.deviceId === desk.deviceId || w.id === desk.deviceId || w.deviceName === desk.deviceId);
  };

  // Clean friendly device badge (e.g. VN1, VN2, VC00)
  const getDeviceDisplayTag = (w: any, desk: Desk) => {
    if (w?.deviceName && w.deviceName.trim()) return w.deviceName;
    if (w?.hostname && w.hostname.trim()) return w.hostname;
    if (desk.deviceId && desk.deviceId.length <= 10) return desk.deviceId;
    return null;
  };

  // Sound ping trigger
  const handleRingDevice = async (deviceId: string, deviceName?: string) => {
    try {
      setRingingDeviceId(deviceId);
      setRingFeedback(null);
      haptics.impact();
      sounds.playTapSound();
      const res = await api.admin.ringDevice(deviceId);
      sounds.playChimeSound();
      setRingFeedback(res.message || `Audio alert command dispatched to ${deviceName || 'workstation'}!`);
      setTimeout(() => setRingFeedback(null), 6000);
    } catch (err: any) {
      alert(err.message || 'Failed to trigger sound alert');
    } finally {
      setRingingDeviceId(null);
    }
  };

  const handleTriggerDeskSound = async (e: React.MouseEvent, w: any, deskId: string) => {
    e.stopPropagation();
    try {
      haptics.impact();
      sounds.playTapSound();
      setPingSuccessDeskId(deskId);
      const targetId = w.deviceId || w.id || deskId;
      const res = await api.admin.ringDevice(targetId);
      sounds.playChimeSound();
      setRingFeedback(res.message || `Audio chime dispatched to ${w.deviceName || w.hostname || 'workstation'}!`);
      setTimeout(() => {
        setPingSuccessDeskId(null);
        setRingFeedback(null);
      }, 5000);
    } catch (err: any) {
      console.error('Failed to trigger sound:', err);
      setPingSuccessDeskId(null);
      alert(err.message || 'Failed to dispatch audio chime to workstation.');
    }
  };

  // Break Approvals
  const handleApproveBreak = async (e: React.MouseEvent, wId: string) => {
    e.stopPropagation();
    setActionLoadingId(wId);
    try {
      haptics.notificationSuccess();
      await api.admin.approveBreak(wId);
      sounds.playChimeSound();
      await loadData(true);
    } catch (err) {
      console.error('Failed to approve break:', err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeclineBreak = async (e: React.MouseEvent, wId: string) => {
    e.stopPropagation();
    setActionLoadingId(wId);
    try {
      haptics.impact();
      await api.admin.declineBreak(wId);
      await loadData(true);
    } catch (err) {
      console.error('Failed to decline break:', err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenDeskSheet = (desk: Desk) => {
    if (isEditMode) return;
    const w = getWorkstationForDesk(desk);
    if (!w) return;

    haptics.selection();
    setSheetWorkstation({
      id: w.id,
      deviceId: w.deviceId,
      hostname: w.hostname,
      deviceName: w.deviceName,
      os: w.os,
      agentVersion: w.agentVersion,
      status: w.status,
      lastSeenAt: w.lastSeenAt,
      lastActivityAt: w.lastActivityAt,
      user: w.user,
      activeInactivity: w.activeInactivity,
    });
    setSheetOpen(true);
  };

  // Update current office helper
  const updateCurrentOffice = (patch: Partial<OfficeFloor>) => {
    setOffices((prev) =>
      prev.map((o) => (o.id === activeOfficeId ? { ...o, ...patch } : o))
    );
  };

  // Pointer Drag & Drop for Desks
  const handleDeskPointerDown = (e: React.PointerEvent, desk: Desk) => {
    if (!isEditMode) return;
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;

    e.preventDefault();
    e.stopPropagation();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {}

    setDraggingDeskId(desk.id);
    setDragStartCoord({ x: e.clientX, y: e.clientY });
    setDragTargetCell({ col: desk.x, row: desk.y });

    if (canvasRef.current) {
      const leftPct = ((desk.x - 1) / gridWidth) * 100;
      const topPct = ((desk.y - 1) / gridHeight) * 100;
      setDragGhostPos({ leftPct, topPct });
    }

    haptics.impact();
  };

  const handleDeskPointerMove = (e: React.PointerEvent, desk: Desk) => {
    if (draggingDeskId !== desk.id || !canvasRef.current) return;
    e.preventDefault();

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Center desk (width ~172px, height ~130px)
    const targetX = mouseX - 86;
    const targetY = mouseY - 25;

    const clampedX = Math.max(0, Math.min(rect.width - 175, targetX));
    const clampedY = Math.max(0, Math.min(rect.height - 135, targetY));

    const leftPct = (clampedX / rect.width) * 100;
    const topPct = (clampedY / rect.height) * 100;

    const colStep = rect.width / gridWidth;
    const rowStep = rect.height / gridHeight;

    const snapCol = Math.max(1, Math.min(gridWidth - 1, Math.round(clampedX / colStep) + 1));
    const snapRow = Math.max(1, Math.min(gridHeight - 1, Math.round(clampedY / rowStep) + 1));

    setDragGhostPos({ leftPct, topPct });
    setDragTargetCell({ col: snapCol, row: snapRow });
  };

  const handleDeskPointerUp = (e: React.PointerEvent, desk: Desk) => {
    if (draggingDeskId !== desk.id) return;
    e.preventDefault();
    e.stopPropagation();

    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}

    const distMoved = dragStartCoord
      ? Math.hypot(e.clientX - dragStartCoord.x, e.clientY - dragStartCoord.y)
      : 0;

    if (distMoved < 6) {
      // Clicked in edit mode -> Open Desk Configuration
      setEditingDesk(desk);
      setShowDeskModal(true);
    } else if (dragTargetCell) {
      // Detect target cabin
      const matchingCabin = currentOffice.cabins.find(
        (c) =>
          dragTargetCell.col >= c.x &&
          dragTargetCell.col <= c.x + c.w &&
          dragTargetCell.row >= c.y &&
          dragTargetCell.row <= c.y + c.h
      );

      const updatedDesks = currentOffice.desks.map((d) =>
        d.id === desk.id
          ? {
              ...d,
              x: dragTargetCell.col,
              y: dragTargetCell.row,
              cabinId: matchingCabin ? matchingCabin.id : d.cabinId,
            }
          : d
      );

      updateCurrentOffice({ desks: updatedDesks });
      haptics.notificationSuccess();
      sounds.playTapSound();
    }

    setDraggingDeskId(null);
    setDragGhostPos(null);
    setDragTargetCell(null);
    setDragStartCoord(null);
  };

  // Add / Create New Office Floor
  const handleCreateOffice = () => {
    if (!newOfficeName.trim()) return;

    let templateCabins: Cabin[] = [];
    if (newOfficeTemplate === 'EXECUTIVE') {
      templateCabins = [
        { id: `cab-${Date.now()}-1`, name: 'CEO & Managing Cabin', type: 'cabin', color: 'purple', x: 1, y: 1, w: 4, h: 3.5 },
        { id: `cab-${Date.now()}-2`, name: 'VP Engineering Cabin', type: 'cabin', color: 'indigo', x: 1, y: 4.8, w: 4, h: 3.2 },
        { id: `cab-${Date.now()}-3`, name: 'Core Operations Bay', type: 'bay', color: 'emerald', x: 5.5, y: 1, w: 6.5, h: 7 },
      ];
    } else if (newOfficeTemplate === 'TECH') {
      templateCabins = [
        { id: `cab-${Date.now()}-1`, name: 'Frontend & UI Pod', type: 'bay', color: 'cyan', x: 1, y: 1, w: 5, h: 5 },
        { id: `cab-${Date.now()}-2`, name: 'Backend & Cloud Pod', type: 'bay', color: 'emerald', x: 6.5, y: 1, w: 5.5, h: 5 },
        { id: `cab-${Date.now()}-3`, name: 'Sprint Meeting Room', type: 'conference', color: 'purple', x: 1, y: 6.2, w: 11, h: 2 },
      ];
    }

    const newOfficeId = `office-${Date.now()}`;
    const newOffice: OfficeFloor = {
      id: newOfficeId,
      name: newOfficeName.trim(),
      floorLevel: newOfficeFloor.trim() || 'Floor 1',
      cabins: templateCabins,
      desks: [],
    };

    setOffices([...offices, newOffice]);
    setActiveOfficeId(newOfficeId);
    setShowOfficeModal(false);
    setNewOfficeName('');
    haptics.notificationSuccess();
    sounds.playChimeSound();
  };

  // Delete current office
  const handleDeleteOffice = (officeId: string) => {
    if (offices.length <= 1) {
      alert('You must have at least one office floor space.');
      return;
    }
    if (confirm(`Are you sure you want to delete this office space?`)) {
      const remaining = offices.filter((o) => o.id !== officeId);
      setOffices(remaining);
      setActiveOfficeId(remaining[0].id);
      haptics.tap();
    }
  };

  // Add / Save Cabin
  const handleSaveCabin = (cabin: Cabin) => {
    const existing = currentOffice.cabins.find((c) => c.id === cabin.id);
    let updatedCabins: Cabin[];
    if (existing) {
      updatedCabins = currentOffice.cabins.map((c) => (c.id === cabin.id ? cabin : c));
    } else {
      updatedCabins = [...currentOffice.cabins, cabin];
    }
    updateCurrentOffice({ cabins: updatedCabins });
    setShowCabinModal(false);
    setEditingCabin(null);
    haptics.notificationSuccess();
  };

  // Delete Cabin
  const handleDeleteCabin = (cabinId: string) => {
    const updatedCabins = currentOffice.cabins.filter((c) => c.id !== cabinId);
    updateCurrentOffice({ cabins: updatedCabins });
    setShowCabinModal(false);
    setEditingCabin(null);
    haptics.tap();
  };

  // Save Desk
  const handleSaveDesk = (desk: Desk) => {
    const existing = currentOffice.desks.find((d) => d.id === desk.id);
    let updatedDesks: Desk[];
    if (existing) {
      updatedDesks = currentOffice.desks.map((d) => (d.id === desk.id ? desk : d));
    } else {
      updatedDesks = [...currentOffice.desks, desk];
    }
    updateCurrentOffice({ desks: updatedDesks });
    setShowDeskModal(false);
    setEditingDesk(null);
    haptics.notificationSuccess();
  };

  // Delete Desk
  const handleDeleteDesk = (deskId: string) => {
    const updatedDesks = currentOffice.desks.filter((d) => d.id !== deskId);
    updateCurrentOffice({ desks: updatedDesks });
    setShowDeskModal(false);
    setEditingDesk(null);
    haptics.tap();
  };

  // Presets applicator
  const applyPresetLayout = (preset: 'EXECUTIVE_CABINS' | 'TECH_STUDIO' | 'OPEN_FLOOR') => {
    haptics.tap();
    if (preset === 'EXECUTIVE_CABINS') {
      const cabins: Cabin[] = [
        { id: `c-${Date.now()}-1`, name: 'Managing Director Cabin', type: 'cabin', color: 'purple', x: 1, y: 1, w: 3.8, h: 3.5 },
        { id: `c-${Date.now()}-2`, name: 'Executive Suite B', type: 'cabin', color: 'indigo', x: 1, y: 4.8, w: 3.8, h: 3.2 },
        { id: `c-${Date.now()}-3`, name: 'Operations & Engineering Bay', type: 'bay', color: 'emerald', x: 5.2, y: 1, w: 4.2, h: 7 },
        { id: `c-${Date.now()}-4`, name: 'Glass Boardroom', type: 'conference', color: 'cyan', x: 9.7, y: 1, w: 3.3, h: 4 },
        { id: `c-${Date.now()}-5`, name: 'Cafe & Wellness Lounge', type: 'lounge', color: 'amber', x: 9.7, y: 5.2, w: 3.3, h: 2.8 },
      ];

      const desks: Desk[] = workstations.map((w, idx) => ({
        id: `desk-${idx + 1}`,
        label: `Station ${String(idx + 1).padStart(2, '0')}`,
        deviceId: w.deviceId,
        x: idx === 0 ? 2 : idx === 1 ? 6 : idx === 2 ? 6 : 2,
        y: idx === 0 ? 2.2 : idx === 1 ? 2.2 : idx === 2 ? 5 : 5.8,
        cabinId: idx === 0 ? cabins[0].id : idx === 3 ? cabins[1].id : cabins[2].id,
      }));

      updateCurrentOffice({ cabins, desks });
    } else if (preset === 'TECH_STUDIO') {
      const cabins: Cabin[] = [
        { id: `c-${Date.now()}-1`, name: 'Alpha Squad Bay', type: 'bay', color: 'cyan', x: 1, y: 1, w: 5.2, h: 5 },
        { id: `c-${Date.now()}-2`, name: 'Beta Squad Bay', type: 'bay', color: 'emerald', x: 6.8, y: 1, w: 5.2, h: 5 },
        { id: `c-${Date.now()}-3`, name: 'Strategy Suite', type: 'conference', color: 'purple', x: 1, y: 6.2, w: 11, h: 2 },
      ];

      const desks: Desk[] = workstations.map((w, idx) => ({
        id: `desk-${idx + 1}`,
        label: `Pod Desk ${idx + 1}`,
        deviceId: w.deviceId,
        x: idx % 2 === 0 ? (idx < 2 ? 2.2 : 8) : idx < 2 ? 3.8 : 9.8,
        y: idx < 2 ? (idx % 2 === 0 ? 2.2 : 3.8) : (idx % 2 === 0 ? 2.2 : 3.8),
        cabinId: idx < 2 ? cabins[0].id : cabins[1].id,
      }));

      updateCurrentOffice({ cabins, desks });
    } else {
      // OPEN FLOOR
      const cabins: Cabin[] = [
        { id: `c-${Date.now()}-1`, name: 'Main Operational Floor A', type: 'bay', color: 'blue', x: 1, y: 1, w: 11, h: 3.5 },
        { id: `c-${Date.now()}-2`, name: 'Main Operational Floor B', type: 'bay', color: 'indigo', x: 1, y: 5, w: 11, h: 3.5 },
      ];

      const desks: Desk[] = workstations.map((w, idx) => ({
        id: `desk-${idx + 1}`,
        label: `Desk ${idx + 1}`,
        deviceId: w.deviceId,
        x: (idx % 4) * 2.6 + 2,
        y: idx < 4 ? 2.2 : 6.2,
        cabinId: idx < 4 ? cabins[0].id : cabins[1].id,
      }));

      updateCurrentOffice({ cabins, desks });
    }
  };

  // Format break remaining
  const getBreakCountdown = (expiresAtStr?: string | null) => {
    if (!expiresAtStr) return '';
    const diff = Math.max(0, Math.floor((new Date(expiresAtStr).getTime() - nowMs) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${mins}m ${secs}s left`;
  };

  // Format idle duration
  const getIdleFormatted = (startedAtStr?: string | null) => {
    if (!startedAtStr) return '00:00';
    const elapsed = Math.max(0, Math.floor((nowMs - new Date(startedAtStr).getTime()) / 1000));
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getCabinIcon = (type: CabinType) => {
    switch (type) {
      case 'cabin':
        return <DoorOpen className="w-3.5 h-3.5" />;
      case 'conference':
        return <Briefcase className="w-3.5 h-3.5" />;
      case 'lounge':
        return <Coffee className="w-3.5 h-3.5" />;
      case 'server':
        return <ShieldAlert className="w-3.5 h-3.5" />;
      default:
        return <Users className="w-3.5 h-3.5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
        <p className="text-xs font-semibold text-zinc-500">Mapping Live Workspace Architect...</p>
      </div>
    );
  }

  return (
    <div className={`space-y-5 ${isFullscreen ? 'fixed inset-0 z-50 bg-zinc-950 p-6 overflow-y-auto' : ''}`}>
      {/* Mobile Experience Notice */}
      <div className="lg:hidden p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-between gap-3 text-xs shadow-sm">
        <div className="flex items-center gap-2.5">
          <Monitor className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-bold">Desktop Recommended</p>
            <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80">
              The 2D Office Floor Radar is designed for desktop and tablet screens.
            </p>
          </div>
        </div>
        <Link
          to="/admin/live"
          className="shrink-0 px-2.5 py-1.5 rounded-xl bg-amber-500 text-white font-bold text-[11px] shadow-sm hover:bg-amber-400 ios-press"
        >
          Live Monitor
        </Link>
      </div>

      {/* Top Header & Office Level Navigation Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-indigo-500 uppercase tracking-wider mb-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
            </span>
            <span>Live Workspace Radar</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black text-zinc-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>Office Floor Plan</span>
              <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                Architectural Radar
              </span>
            </h1>

            {/* Office Space Switcher Dropdown */}
            <div className="relative inline-block">
              <select
                value={activeOfficeId}
                onChange={(e) => {
                  setActiveOfficeId(e.target.value);
                  haptics.selection();
                }}
                className="pl-3 pr-8 py-1.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-800 dark:text-zinc-200 appearance-none cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {offices.map((off) => (
                  <option key={off.id} value={off.id}>
                    🏢 {off.name} ({off.floorLevel})
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* New Office / Floor Button */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => setShowOfficeModal(true)}
                className="px-2.5 py-1.5 rounded-xl text-xs font-bold bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 flex items-center gap-1 transition"
                title="Create a new office space or floor"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Office</span>
              </button>
            )}
          </div>

          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            Real-time physical desk radar & interactive cabin architecture across {user?.organization?.name || 'Company'} headquarters
          </p>
        </div>

        {/* Action Controls & Studio Mode Switcher */}
        <div className="flex items-center flex-wrap gap-2">
          {isEditMode ? (
            <>
              {/* Layout Presets Dropdown */}
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-xs font-bold">
                <span className="text-[10px] text-zinc-400 px-2 uppercase">Presets:</span>
                <button
                  type="button"
                  onClick={() => applyPresetLayout('EXECUTIVE_CABINS')}
                  className="px-2.5 py-1 rounded-lg hover:bg-white dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 transition"
                  title="Director Cabins + Dev Pod"
                >
                  Cabins
                </button>
                <button
                  type="button"
                  onClick={() => applyPresetLayout('TECH_STUDIO')}
                  className="px-2.5 py-1 rounded-lg hover:bg-white dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 transition"
                  title="Squad Bays + Strategy Suite"
                >
                  Tech Hub
                </button>
                <button
                  type="button"
                  onClick={() => applyPresetLayout('OPEN_FLOOR')}
                  className="px-2.5 py-1 rounded-lg hover:bg-white dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 transition"
                  title="Open Operational Rows"
                >
                  Open Rows
                </button>
              </div>

              {/* Add Cabin Button */}
              <button
                type="button"
                onClick={() => {
                  setEditingCabin({
                    id: `cab-${Date.now()}`,
                    name: `Executive Cabin ${currentOffice.cabins.length + 1}`,
                    type: 'cabin',
                    color: 'indigo',
                    x: 1,
                    y: 1,
                    w: 4,
                    h: 3.5,
                  });
                  setShowCabinModal(true);
                  haptics.tap();
                }}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-1.5 transition shadow-sm"
              >
                <DoorOpen className="w-3.5 h-3.5 text-indigo-400" />
                <span>+ Add Cabin</span>
              </button>

              {/* Add Desk Button */}
              <button
                type="button"
                onClick={() => {
                  setEditingDesk({
                    id: `desk-${Date.now()}`,
                    label: `Desk ${String(currentOffice.desks.length + 1).padStart(2, '0')}`,
                    deviceId: null,
                    x: 6,
                    y: 3,
                  });
                  setShowDeskModal(true);
                  haptics.tap();
                }}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-1.5 transition shadow-sm"
              >
                <Monitor className="w-3.5 h-3.5 text-emerald-400" />
                <span>+ Add Desk</span>
              </button>

              {/* Save Layout Button */}
              <button
                type="button"
                onClick={handleSaveFloorPlan}
                disabled={saving}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white flex items-center gap-1.5 shadow-sm transition disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : saveSuccess ? (
                  <Check className="w-3.5 h-3.5 text-emerald-300" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                <span>{saveSuccess ? 'Saved to Cloud!' : 'Save Layout'}</span>
              </button>

              <button
                type="button"
                onClick={() => setIsEditMode(false)}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-300 transition"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setIsEditMode(true);
                  haptics.impact();
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white flex items-center gap-1.5 shadow-md shadow-indigo-500/20 transition"
              >
                <Sliders className="w-3.5 h-3.5" />
                <span>Design & Edit Office</span>
              </button>

              <button
                type="button"
                onClick={() => loadData()}
                disabled={refreshing}
                className="p-2 rounded-xl text-zinc-500 hover:text-zinc-800 dark:hover:text-white bg-zinc-100 dark:bg-zinc-800 transition"
                title="Refresh Radar"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              <button
                type="button"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 rounded-xl text-zinc-500 hover:text-zinc-800 dark:hover:text-white bg-zinc-100 dark:bg-zinc-800 transition"
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Wallboard'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Live Status Metric Badges */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
        <div className="p-3 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800 flex items-center gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center font-bold">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-zinc-400">Active Desks</div>
            <div className="text-base font-black text-emerald-600 dark:text-emerald-400">{metrics.active}</div>
          </div>
        </div>

        <div className="p-3 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800 flex items-center gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center font-bold">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-zinc-400">Idle / Inactive</div>
            <div className="text-base font-black text-amber-600 dark:text-amber-400">{metrics.inactive}</div>
          </div>
        </div>

        <div className="p-3 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800 flex items-center gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center font-bold">
            <Coffee className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-zinc-400">On Break</div>
            <div className="text-base font-black text-blue-600 dark:text-blue-400">{metrics.onBreak}</div>
          </div>
        </div>

        <div className="p-3 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800 flex items-center gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center font-bold">
            <Moon className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-zinc-400">Shift Ended</div>
            <div className="text-base font-black text-indigo-600 dark:text-indigo-400">{metrics.shiftEnded}</div>
          </div>
        </div>

        <div className="p-3 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800 flex items-center gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-xl bg-zinc-500/10 text-zinc-500 flex items-center justify-center font-bold">
            <Monitor className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-zinc-400">Offline</div>
            <div className="text-base font-black text-zinc-500">{metrics.offline}</div>
          </div>
        </div>

        <div className="p-3 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800 flex items-center gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-xl bg-violet-500/10 text-violet-500 flex items-center justify-center font-bold">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-zinc-400">Total Desks</div>
            <div className="text-base font-black text-zinc-900 dark:text-white">{currentOffice.desks.length}</div>
          </div>
        </div>
      </div>

      {/* Main Architectural Floor Canvas Container */}
      <div className="p-4 sm:p-6 rounded-3xl bg-zinc-950 border border-zinc-800/90 shadow-2xl relative overflow-hidden select-none">
        {/* Top Blueprint Ruler & Office Status */}
        <div className="flex items-center justify-between text-[11px] font-mono text-zinc-500 px-3 pb-3 border-b border-zinc-800/70">
          <div className="flex items-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-zinc-300 font-bold uppercase tracking-wider">
              {currentOffice.name} • {currentOffice.floorLevel}
            </span>
            <span className="text-zinc-600">|</span>
            <span className="text-zinc-400">
              {currentOffice.cabins.length} Cabins & Rooms
            </span>
          </div>

          <div className="flex items-center gap-4 text-zinc-500">
            <span>Grid: {gridWidth} × {gridHeight}</span>
            {isEditMode && (
              <span className="text-indigo-400 font-bold animate-pulse">
                Studio Mode: Drag to Rearrange
              </span>
            )}
          </div>
        </div>

        {/* The Spatial Canvas Floor */}
        <div
          ref={canvasRef}
          className="relative w-full rounded-2xl mt-3 overflow-hidden bg-[#07080c] border border-zinc-800/80 shadow-inner"
          style={{ minHeight: '620px', height: '640px' }}
        >
          {/* Architectural Micro-Dot Floor Grid Texture */}
          <div
            className="absolute inset-0 pointer-events-none opacity-25"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.25) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />

          {/* Cabins & Rooms Architectural Partitions */}
          {currentOffice.cabins.map((cabin) => {
            const style = CABIN_COLORS[cabin.color] || CABIN_COLORS.emerald;
            const leftPct = ((cabin.x - 1) / gridWidth) * 100;
            const topPct = ((cabin.y - 1) / gridHeight) * 100;
            const widthPct = (cabin.w / gridWidth) * 100;
            const heightPct = (cabin.h / gridHeight) * 100;

            const cabinDesks = currentOffice.desks.filter((d) => d.cabinId === cabin.id);
            const activeInCabin = cabinDesks.filter((d) => {
              const w = getWorkstationForDesk(d);
              return w?.status === 'ACTIVE' && (!w?.breakStatus || w?.breakStatus === 'NONE');
            }).length;

            return (
              <div
                key={cabin.id}
                className={`absolute rounded-2xl border ${style.border} ${style.bg} transition-all p-3 overflow-hidden flex flex-col justify-between`}
                style={{
                  left: `${leftPct}%`,
                  top: `${topPct}%`,
                  width: `${widthPct}%`,
                  height: `${heightPct}%`,
                  boxShadow: `inset 0 0 45px ${style.glow}`,
                }}
              >
                {/* Cabin Header Glass Partition */}
                <div className="flex items-center justify-between gap-1.5 z-10 bg-zinc-950/70 border border-zinc-800/80 rounded-xl px-2.5 py-1.5 backdrop-blur-md">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`${style.text}`}>{getCabinIcon(cabin.type)}</span>
                    <span className="text-[11px] font-black uppercase tracking-wider text-zinc-200 truncate">
                      {cabin.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">
                      {cabinDesks.length} Desks {activeInCabin > 0 ? `• ${activeInCabin} Live` : ''}
                    </span>

                    {/* In Edit Mode: Configure & Delete Cabin */}
                    {isEditMode && (
                      <div className="flex items-center gap-0.5 ml-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCabin(cabin);
                            setShowCabinModal(true);
                          }}
                          className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition"
                          title="Configure Cabin"
                        >
                          <Settings2 className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCabin(cabin.id)}
                          className="p-1 rounded hover:bg-rose-500/20 text-zinc-400 hover:text-rose-400 transition"
                          title="Delete Cabin"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Architectural Room Floor Ambience */}
                <div className="flex-1 flex items-center justify-center pointer-events-none opacity-25">
                  {cabin.type === 'conference' && (
                    <div className="w-3/5 h-2/5 rounded-2xl border border-purple-500/40 bg-purple-900/10 flex items-center justify-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-[10px] font-bold text-purple-300 font-mono tracking-wider uppercase">
                        Meeting Table
                      </span>
                    </div>
                  )}
                  {cabin.type === 'lounge' && (
                    <div className="w-3/5 h-2/5 rounded-2xl border border-amber-500/40 bg-amber-900/10 flex items-center justify-center gap-1.5">
                      <Coffee className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-[10px] font-bold text-amber-300 font-mono tracking-wider uppercase">
                        Pantry Station
                      </span>
                    </div>
                  )}
                </div>

                {/* Architectural Doorway Opening Indicator */}
                <div className="flex items-center justify-center">
                  <div className="h-0.5 w-10 bg-zinc-600/50 rounded-full" />
                </div>
              </div>
            );
          })}

          {/* Snapped Destination Drop Target Indicator during Drag */}
          {draggingDeskId && dragTargetCell && (
            <div
              className="absolute rounded-2xl border-2 border-dashed border-indigo-400 bg-indigo-500/15 pointer-events-none z-30 transition-all duration-75 flex flex-col items-center justify-center shadow-lg shadow-indigo-500/20"
              style={{
                left: `${((dragTargetCell.col - 1) / gridWidth) * 100}%`,
                top: `${((dragTargetCell.row - 1) / gridHeight) * 100}%`,
                width: '175px',
                height: '135px',
              }}
            >
              <div className="px-2 py-1 rounded bg-indigo-600 text-white font-mono text-[10px] font-bold shadow">
                Drop at Col {dragTargetCell.col}, Row {dragTargetCell.row}
              </div>
            </div>
          )}

          {/* Desks Layer (Executive Workstations) */}
          <div className="absolute inset-0 pointer-events-auto z-40">
            {currentOffice.desks.map((desk) => {
              const workstation = getWorkstationForDesk(desk);
              const deviceTag = getDeviceDisplayTag(workstation, desk);

              const isDragging = draggingDeskId === desk.id;

              const leftPct = isDragging && dragGhostPos
                ? dragGhostPos.leftPct
                : ((desk.x - 1) / gridWidth) * 100;

              const topPct = isDragging && dragGhostPos
                ? dragGhostPos.topPct
                : ((desk.y - 1) / gridHeight) * 100;

              // Telemetry status variables
              const isInactive = workstation?.status === 'INACTIVE' && (!workstation?.breakStatus || workstation?.breakStatus === 'NONE');
              const isActive = workstation?.status === 'ACTIVE' && (!workstation?.breakStatus || workstation?.breakStatus === 'NONE');
              const isBreak = Boolean(workstation?.breakStatus && workstation?.breakStatus !== 'NONE' && workstation?.status !== 'OFFLINE');
              const isShiftEnded = workstation?.status === 'SHIFT_ENDED' || workstation?.status === 'DORMANT';
              const isPendingBreak = workstation?.breakStatus === 'PENDING_APPROVAL';

              return (
                <div
                  key={desk.id}
                  onPointerDown={(e) => handleDeskPointerDown(e, desk)}
                  onPointerMove={(e) => handleDeskPointerMove(e, desk)}
                  onPointerUp={(e) => handleDeskPointerUp(e, desk)}
                  onClick={() => handleOpenDeskSheet(desk)}
                  className={`absolute flex flex-col items-center group transition-transform ${
                    isDragging
                      ? 'z-50 scale-105 cursor-grabbing opacity-90'
                      : isEditMode
                      ? 'cursor-grab hover:scale-[1.03] z-30'
                      : 'cursor-pointer hover:scale-[1.03] z-20'
                  }`}
                  style={{
                    left: `${leftPct}%`,
                    top: `${topPct}%`,
                    width: '175px',
                    height: '135px',
                    touchAction: 'none',
                  }}
                >
                  {/* Clean Modern Physical Desk Card */}
                  <div
                    className={`w-full flex-1 rounded-2xl p-2.5 flex flex-col justify-between relative transition-all duration-200 backdrop-blur-md shadow-lg ${
                      isDragging
                        ? 'bg-zinc-900 border-2 border-indigo-400 shadow-2xl ring-4 ring-indigo-500/30'
                        : isEditMode
                        ? 'bg-zinc-900/95 border border-dashed border-indigo-500/50 hover:border-indigo-400'
                        : isActive
                        ? 'bg-zinc-900/95 border border-emerald-500/50 shadow-emerald-500/10 ring-1 ring-emerald-500/20'
                        : isInactive
                        ? 'bg-zinc-900/95 border border-amber-500/50 shadow-amber-500/10 ring-1 ring-amber-500/30'
                        : isBreak
                        ? 'bg-zinc-900/95 border border-blue-500/50 shadow-blue-500/10 ring-1 ring-blue-500/20'
                        : isShiftEnded
                        ? 'bg-zinc-900/95 border border-indigo-500/30'
                        : 'bg-zinc-900/90 border border-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    {/* Top Hardware LED Rim */}
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-black text-zinc-100 font-mono tracking-tight">
                          {desk.label}
                        </span>
                        {deviceTag && (
                          <span className="text-[9px] font-mono font-black px-1.5 py-0.2 rounded bg-zinc-800 text-indigo-300 border border-indigo-500/20">
                            {deviceTag}
                          </span>
                        )}
                      </div>

                      {/* Status Beacon Dot */}
                      <div className="flex items-center gap-1">
                        {isEditMode ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingDesk(desk);
                              setShowDeskModal(true);
                            }}
                            className="p-0.5 rounded text-zinc-400 hover:text-white"
                          >
                            <Settings2 className="w-3 h-3" />
                          </button>
                        ) : (
                          <span
                            className={`w-2 h-2 rounded-full ${
                              isActive
                                ? 'bg-emerald-400 animate-pulse'
                                : isInactive
                                ? 'bg-amber-400 animate-ping'
                                : isBreak
                                ? 'bg-blue-400'
                                : isShiftEnded
                                ? 'bg-indigo-400'
                                : 'bg-zinc-600'
                            }`}
                          />
                        )}
                      </div>
                    </div>

                    {/* Occupant Details & Machine Info */}
                    {workstation ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {/* Avatar Circle with Status Ring */}
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 border ${
                              isActive
                                ? 'bg-emerald-950 border-emerald-500 text-emerald-300 ring-2 ring-emerald-500/30'
                                : isInactive
                                ? 'bg-amber-950 border-amber-500 text-amber-300 ring-2 ring-amber-500/30'
                                : isBreak
                                ? 'bg-blue-950 border-blue-500 text-blue-300 ring-2 ring-blue-500/30'
                                : isShiftEnded
                                ? 'bg-indigo-950 border-indigo-500 text-indigo-300'
                                : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                            }`}
                          >
                            {workstation.user?.fullName?.charAt(0) || workstation.deviceName?.charAt(0) || 'W'}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-zinc-100 truncate">
                              {workstation.user?.fullName || workstation.deviceName || workstation.hostname}
                            </div>
                            <div className="text-[9px] text-zinc-400 truncate">
                              {workstation.user?.department || workstation.hostname || 'Workstation'}
                            </div>
                          </div>
                        </div>

                        {/* Real-time Ticker: Inactivity, Break countdown, or Live Active */}
                        <div className="pt-1 flex items-center justify-between text-[10px] font-mono border-t border-zinc-800/80">
                          {isPendingBreak ? (
                            <span className="text-amber-400 font-bold animate-pulse">Break Req</span>
                          ) : isInactive ? (
                            <span className="text-amber-400 font-bold flex items-center gap-1">
                              <span>⏱️</span>
                              <span>{getIdleFormatted(workstation.activeInactivity?.startedAt || workstation.lastActivityAt)}</span>
                            </span>
                          ) : isBreak ? (
                            <span className="text-blue-400 font-bold flex items-center gap-1">
                              <span>☕</span>
                              <span>{getBreakCountdown(workstation.breakExpiresAt)}</span>
                            </span>
                          ) : isActive ? (
                            <span className="text-emerald-400 font-bold flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              <span>Live Active</span>
                            </span>
                          ) : isShiftEnded ? (
                            <span className="text-indigo-400 flex items-center gap-1">
                              <span>🌙</span>
                              <span>Off-Duty</span>
                            </span>
                          ) : (
                            <span className="text-zinc-500 text-[9px]">Offline</span>
                          )}

                          {/* Quick Sound Chime Action Button */}
                          <button
                            type="button"
                            onClick={(e) => handleTriggerDeskSound(e, workstation, desk.id)}
                            className={`p-1 rounded-md transition ${
                              pingSuccessDeskId === desk.id
                                ? 'bg-emerald-500 text-white animate-bounce'
                                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                            }`}
                            title="Ping audio alert on workstation"
                          >
                            <Volume2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Quick Break Approval Bar if Requested */}
                        {isPendingBreak && (
                          <div className="mt-1 pt-1 border-t border-amber-500/20 flex items-center justify-between gap-1">
                            <span className="text-[9px] text-amber-300 font-bold">
                              {workstation.breakRequestedDuration || 10}m Break?
                            </span>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={(e) => handleApproveBreak(e, workstation.id)}
                                disabled={actionLoadingId === workstation.id}
                                className="px-1.5 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[9px] font-bold"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={(e) => handleDeclineBreak(e, workstation.id)}
                                disabled={actionLoadingId === workstation.id}
                                className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[9px]"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-2 text-zinc-500">
                        <span className="text-[10px] font-semibold text-zinc-400">Hot Desk</span>
                        <span className="text-[9px] text-zinc-500">
                          {isEditMode ? '+ Drag or Click' : 'Available'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Office Floor Creator / Switcher Modal */}
      {showOfficeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-ios-fade">
          <div className="w-full max-w-md p-6 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-zinc-900 dark:text-white">Create Office Space</h3>
                  <p className="text-[11px] text-zinc-500">Add a new floor, wing, or corporate office location</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowOfficeModal(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Office / Building Name
              </label>
              <input
                type="text"
                value={newOfficeName}
                onChange={(e) => setNewOfficeName(e.target.value)}
                placeholder="e.g. Mumbai Tech Center or 2nd Floor Wing"
                className="w-full p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Floor Level
              </label>
              <input
                type="text"
                value={newOfficeFloor}
                onChange={(e) => setNewOfficeFloor(e.target.value)}
                placeholder="e.g. Ground Floor, Level 2, Penthouse"
                className="w-full p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Initial Layout Architecture
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setNewOfficeTemplate('EXECUTIVE')}
                  className={`p-3 rounded-xl border text-center transition ${
                    newOfficeTemplate === 'EXECUTIVE'
                      ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400'
                      : 'border-zinc-200 dark:border-zinc-800 text-zinc-400'
                  }`}
                >
                  <DoorOpen className="w-4 h-4 mx-auto mb-1" />
                  <div className="text-[10px] font-bold">Cabins Suite</div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewOfficeTemplate('TECH')}
                  className={`p-3 rounded-xl border text-center transition ${
                    newOfficeTemplate === 'TECH'
                      ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400'
                      : 'border-zinc-200 dark:border-zinc-800 text-zinc-400'
                  }`}
                >
                  <Users className="w-4 h-4 mx-auto mb-1" />
                  <div className="text-[10px] font-bold">Tech Bays</div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewOfficeTemplate('BLANK')}
                  className={`p-3 rounded-xl border text-center transition ${
                    newOfficeTemplate === 'BLANK'
                      ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400'
                      : 'border-zinc-200 dark:border-zinc-800 text-zinc-400'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4 mx-auto mb-1" />
                  <div className="text-[10px] font-bold">Blank Slate</div>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-200 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => setShowOfficeModal(false)}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateOffice}
                disabled={!newOfficeName.trim()}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
              >
                Create Office
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cabin / Room Customizer Modal */}
      {showCabinModal && editingCabin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-ios-fade">
          <div className="w-full max-w-md p-6 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold">
                  <DoorOpen className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-zinc-900 dark:text-white">Customize Cabin / Room</h3>
                  <p className="text-[11px] text-zinc-500">Design an executive cabin, team bay, or meeting space</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCabinModal(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cabin Name */}
            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Cabin / Room Name
              </label>
              <input
                type="text"
                value={editingCabin.name}
                onChange={(e) => setEditingCabin({ ...editingCabin, name: e.target.value })}
                placeholder="e.g. Director Cabin, DevOps Bay, Boardroom"
                className="w-full p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
              />
            </div>

            {/* Cabin Type */}
            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Room Purpose & Category
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'cabin', label: 'Private Cabin', icon: DoorOpen },
                  { id: 'bay', label: 'Team Bay', icon: Users },
                  { id: 'conference', label: 'Boardroom', icon: Briefcase },
                  { id: 'lounge', label: 'Cafe / Pantry', icon: Coffee },
                  { id: 'server', label: 'DevOps / Server', icon: ShieldAlert },
                ].map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setEditingCabin({ ...editingCabin, type: cat.id as CabinType })}
                      className={`p-2.5 rounded-xl border text-center transition ${
                        editingCabin.type === cat.id
                          ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400 font-bold'
                          : 'border-zinc-200 dark:border-zinc-800 text-zinc-400'
                      }`}
                    >
                      <Icon className="w-4 h-4 mx-auto mb-1" />
                      <div className="text-[10px]">{cat.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Accent Theme Color */}
            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Glass Accent Tint
              </label>
              <div className="flex items-center gap-2">
                {Object.keys(CABIN_COLORS).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEditingCabin({ ...editingCabin, color: c })}
                    className={`w-7 h-7 rounded-full border-2 transition ${
                      editingCabin.color === c ? 'border-white scale-110 shadow-lg' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: CABIN_COLORS[c].tint.replace('bg-', '') }}
                    title={c}
                  />
                ))}
              </div>
            </div>

            {/* Dimensions (Cols & Rows) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Width (Cols 2-8)
                </label>
                <input
                  type="number"
                  min={2}
                  max={8}
                  step={0.5}
                  value={editingCabin.w}
                  onChange={(e) => setEditingCabin({ ...editingCabin, w: Number(e.target.value) || 3 })}
                  className="w-full p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Height (Rows 2-8)
                </label>
                <input
                  type="number"
                  min={2}
                  max={8}
                  step={0.5}
                  value={editingCabin.h}
                  onChange={(e) => setEditingCabin({ ...editingCabin, h: Number(e.target.value) || 3 })}
                  className="w-full p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-zinc-200 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => handleDeleteCabin(editingCabin.id)}
                className="px-3 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-500/10 flex items-center gap-1.5 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Room</span>
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowCabinModal(false)}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveCabin(editingCabin)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  Save Cabin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desk Configuration Modal */}
      {showDeskModal && editingDesk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-ios-fade">
          <div className="w-full max-w-md p-6 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold">
                  <Monitor className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-zinc-900 dark:text-white">Configure Workstation Desk</h3>
                  <p className="text-[11px] text-zinc-500">Position ({editingDesk.x}, {editingDesk.y})</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDeskModal(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Desk Label */}
            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Desk Label / Station Tag
              </label>
              <input
                type="text"
                value={editingDesk.label}
                onChange={(e) => setEditingDesk({ ...editingDesk, label: e.target.value })}
                placeholder="e.g. Desk 01 or Lead Dev Station"
                className="w-full p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
              />
            </div>

            {/* Assigned Device */}
            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Assigned Workstation Device
              </label>
              <select
                value={editingDesk.deviceId || ''}
                onChange={(e) => setEditingDesk({ ...editingDesk, deviceId: e.target.value || null })}
                className="w-full p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
              >
                <option value="">-- Unassigned (Hot Desk) --</option>
                {workstations.map((w) => (
                  <option key={w.deviceId || w.id} value={w.deviceId || w.id}>
                    {w.deviceName || w.hostname} {w.user?.fullName ? `• ${w.user.fullName}` : ''} ({w.hostname})
                  </option>
                ))}
              </select>
            </div>

            {/* Cabin Assignment */}
            <div>
              <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                Cabin / Room Enclosure
              </label>
              <select
                value={editingDesk.cabinId || ''}
                onChange={(e) => setEditingDesk({ ...editingDesk, cabinId: e.target.value || null })}
                className="w-full p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-xs font-bold text-zinc-900 dark:text-white"
              >
                <option value="">-- Open Floor --</option>
                {currentOffice.cabins.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.type})
                  </option>
                ))}
              </select>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-zinc-200 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => handleDeleteDesk(editingDesk.id)}
                className="px-3 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-500/10 flex items-center gap-1.5 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Desk</span>
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeskModal(false)}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveDesk(editingDesk)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  Save Desk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sound Ring Feedback Toast Banner */}
      {ringFeedback && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl bg-indigo-600 text-white shadow-xl shadow-indigo-600/30 animate-ios-fade border border-indigo-400/30">
          <Volume2 className="w-5 h-5 animate-bounce" />
          <span className="text-xs font-semibold">{ringFeedback}</span>
        </div>
      )}

      {/* Slide-over Workstation Telemetry Inspector */}
      <IosBottomSheet
        workstation={sheetWorkstation}
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onRingDevice={handleRingDevice}
        isRinging={ringingDeviceId !== null && ringingDeviceId === (sheetWorkstation?.deviceId || sheetWorkstation?.id)}
        now={nowMs}
      />
    </div>
  );
}
